import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';
import SettingsNav from './SettingsNav';

function EmployerHiringPreferences() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Mobile-only: sidebar drawer toggle (same pattern as other employer pages)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    api
    .get('/company/settings/hiring')
    .then((res) => setPrefs(res.data.hiring_preferences ?? res.data)) // handles both wrapped and unwrapped shapes
    .catch((err) => {
      console.error('Failed to fetch hiring preferences', err);
      setMessage('Could not load hiring preferences. Please refresh.');
    })
    .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const handleChange = (key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const handleRangeChange = (bound, value) => {
    setPrefs((prev) => ({
      ...prev,
      preferred_experience_range: {
        ...(prev.preferred_experience_range || {}),
        [bound]: value === '' ? null : Number(value),
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put('/company/settings/hiring', prefs);
      setPrefs(res.data.hiring_preferences);
      setMessage('Hiring preferences saved.');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8FF]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#3B0764]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} mobile />}

            <h2 className="text-xl sm:text-2xl font-bold mb-1 bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent">Company Settings</h2>
            <p className="text-gray-500 text-sm mb-4">Manage your team and preferences</p>

            <SettingsNav />

            <div className="bg-white rounded-xl shadow p-5 sm:p-6">
              <h3 className="font-bold text-[#3B0764] mb-4">Hiring preferences</h3>

              {message && (
                <div className="bg-[#ECFDF5] text-[#10B981] p-3 rounded-lg mb-4 text-sm">{message}</div>
              )}

              {loading ? (
                <p className="text-gray-500 text-sm">Loading...</p>
              ) : !prefs ? (
                <p className="text-red-500 text-sm">{message || 'Unable to load preferences.'}</p>
              ) : (
                <>
                  <div className="mb-5">
                    <label className="block text-sm font-medium mb-1 text-[#3B0764]">
                      Auto-reject candidates below this AI match score (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={prefs.auto_reject_below_match_score ?? ''}
                      onChange={(e) =>
                        handleChange(
                          'auto_reject_below_match_score',
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                      placeholder="Leave blank to disable"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block text-sm font-medium mb-1 text-[#3B0764]">
                      Default notice period (days)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={prefs.default_notice_period_days ?? ''}
                      onChange={(e) =>
                        handleChange(
                          'default_notice_period_days',
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                      placeholder="e.g. 30"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block text-sm font-medium mb-2 text-[#3B0764]">
                      Preferred experience range (years)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="number"
                        min="0"
                        value={prefs.preferred_experience_range?.min ?? ''}
                        onChange={(e) => handleRangeChange('min', e.target.value)}
                        placeholder="Min"
                        className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                      />
                      <input
                        type="number"
                        min="0"
                        value={prefs.preferred_experience_range?.max ?? ''}
                        onChange={(e) => handleRangeChange('max', e.target.value)}
                        placeholder="Max"
                        className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleChange('require_resume', !prefs.require_resume)}
                    className={`w-full flex items-center justify-between p-3.5 rounded-lg border-2 transition text-left mb-6 ${
                      prefs.require_resume ? 'border-[#8B5CF6] bg-[#F5F3FF] shadow-sm shadow-purple-500/10' : 'border-gray-200 bg-white hover:border-[#C4B5FD] hover:shadow-md hover:shadow-purple-500/10'
                    }`}
                  >
                    <span className={`text-sm font-medium ${prefs.require_resume ? 'text-[#7C3AED]' : 'text-gray-600'}`}>
                      Require a resume to apply
                    </span>
                    <span
                      className={`w-10 h-5 rounded-full relative transition shrink-0 ${
                        prefs.require_resume ? 'bg-gradient-to-r from-[#5B2EFF] to-[#FF4D8D]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                          prefs.require_resume ? 'left-5' : 'left-0.5'
                        }`}
                      />
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] text-white px-6 py-2.5 rounded-[18px] font-semibold text-sm shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/35 hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save changes'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployerHiringPreferences;